import DungeonConfig, { TILE, ROOM_TYPE } from './DungeonConfig.js';
import DungeonTheme from './DungeonTheme.js';

/**
 * DungeonGeometry - Converts a 2D grid + rooms into cuboid definitions.
 * Uses run-length merging for both floors and walls to minimize cuboid count
 * and guarantee non-intersecting geometry.
 */
export default class DungeonGeometry {
  /**
   * Build cuboid definitions from a DungeonLevel.
   * @param {DungeonLevel} level
   * @param {Object} theme - DungeonTheme palette
   * @returns {Array<Object>} cuboidDefs
   */
  static buildCuboids(level, theme) {
    const defs = [];
    const FLOOR_H = DungeonConfig.FLOOR_HEIGHT;
    const WALL_H = DungeonConfig.WALL_HEIGHT;
    // 1. Floor cuboids: merged from grid FLOOR tiles (no overlaps)
    const floorDefs = DungeonGeometry._buildMergedFloors(
      level.grid, level.gridWidth, level.gridHeight, theme, FLOOR_H
    );
    defs.push(...floorDefs);

    // 2. Wall cuboids: run-length merged
    const wallDefs = DungeonGeometry._buildMergedWalls(
      level.grid, level.gridWidth, level.gridHeight, theme, WALL_H
    );
    defs.push(...wallDefs);

    // 3. Portal cuboid in exit room
    const exitRoom = level.rooms.find(r => r.type === ROOM_TYPE.EXIT);
    if (exitRoom) {
      const cx = exitRoom.x + Math.floor(exitRoom.width / 2);
      const cz = exitRoom.z + Math.floor(exitRoom.depth / 2);
      defs.push({
        x: cx,
        y: 0,
        z: cz,
        width: 1,
        height: 2,
        depth: 1,
        materials: DungeonTheme.buildPortalMaterials(theme),
        role: 'portal'
      });
    }

    // 3b. Back-portal cuboid in start room (level 2+ only)
    // Placed 2 tiles away from entry point so it's visible but separate
    if (level.levelIndex > 0) {
      const startRoom = level.rooms.find(r => r.type === ROOM_TYPE.START);
      if (startRoom) {
        const cx = startRoom.x + Math.floor(startRoom.width / 2) + 2;
        const cz = startRoom.z + Math.floor(startRoom.depth / 2);
        defs.push({
          x: cx,
          y: 0,
          z: cz,
          width: 1,
          height: 2,
          depth: 1,
          materials: DungeonTheme.buildBackPortalMaterials(),
          role: 'back-portal'
        });
        // Store back-portal location on level for portal detection
        level.backPortalPoint = { x: cx + 0.5, y: 0, z: cz + 0.5 };
      }
    }

    // 4. Decorative pillars in large rooms
    for (const room of level.rooms) {
      if (room.width >= 8 && room.depth >= 8) {
        const pillarMat = theme.pillar();
        const mats = {
          top: theme.wall.top(), bottom: theme.floor.sides(),
          front: pillarMat, back: pillarMat,
          left: pillarMat, right: pillarMat
        };
        // Place 4 pillars near corners
        const offsets = [
          { dx: 2, dz: 2 }, { dx: room.width - 3, dz: 2 },
          { dx: 2, dz: room.depth - 3 }, { dx: room.width - 3, dz: room.depth - 3 }
        ];
        for (const off of offsets) {
          defs.push({
            x: room.x + off.dx,
            y: 0,
            z: room.z + off.dz,
            width: 1,
            height: WALL_H,
            depth: 1,
            materials: { ...mats },
            role: 'pillar',
            collisionHeight: DungeonConfig.WALL_COLLISION_HEIGHT
          });
        }
      }
    }

    // 5. Chest cuboids in treasure rooms
    for (const room of level.rooms) {
      if (room.type === ROOM_TYPE.TREASURE) {
        const cx = room.x + Math.floor(room.width / 2);
        const cz = room.z + Math.floor(room.depth / 2);
        const chestMat = theme.chest();
        defs.push({
          x: cx,
          y: 0,
          z: cz,
          width: 1,
          height: 0.8,
          depth: 1,
          materials: {
            top: theme.wall.top(),
            bottom: chestMat,
            front: chestMat, back: chestMat,
            left: chestMat, right: chestMat
          },
          role: 'chest'
        });
      }
    }

    // 6. Vegetation impostor sprites (stored separately on level, not as cuboid defs)
    level.vegetationDefs = DungeonGeometry._buildVegetation(level, theme);

    return defs;
  }

  /**
   * Build floor cuboids by run-length merging from grid FLOOR tiles.
   * Scans horizontally to create wide strips, guaranteeing no overlaps.
   * @private
   */
  static _buildMergedFloors(grid, gridWidth, gridHeight, theme, floorHeight) {
    const defs = [];
    const covered = [];
    for (let x = 0; x < gridWidth; x++) {
      covered[x] = new Uint8Array(gridHeight); // 0 = uncovered
    }

    // Pass 1: Greedy rectangle merge — find horizontal runs per row,
    // then extend each run downward (increasing Z) to form 2D rectangles.
    // This produces fewer, larger floor cuboids (e.g. a 10x10 room → 1-3 cuboids
    // instead of 10 depth-1 strips), which greatly improves pathfinding quality.
    for (let z = 0; z < gridHeight; z++) {
      let runStart = null;
      for (let x = 0; x <= gridWidth; x++) {
        const isFloor = x < gridWidth && grid[x][z] === TILE.FLOOR && !covered[x][z];
        if (isFloor && runStart === null) {
          runStart = x;
        } else if (!isFloor && runStart !== null) {
          const runWidth = x - runStart;
          // Extend this run downward (increasing Z) as far as possible
          let runDepth = 1;
          let canExtend = true;
          while (canExtend && z + runDepth < gridHeight) {
            // Check if the entire next row has matching FLOOR tiles
            for (let rx = runStart; rx < runStart + runWidth; rx++) {
              if (grid[rx][z + runDepth] !== TILE.FLOOR || covered[rx][z + runDepth]) {
                canExtend = false;
                break;
              }
            }
            if (canExtend) runDepth++;
          }
          // Mark all covered tiles
          for (let rz = z; rz < z + runDepth; rz++) {
            for (let rx = runStart; rx < runStart + runWidth; rx++) {
              covered[rx][rz] = 1;
            }
          }
          defs.push({
            x: runStart,
            y: -floorHeight,
            z: z,
            width: runWidth,
            height: floorHeight,
            depth: runDepth,
            materials: DungeonTheme.buildFloorMaterials(theme),
            role: 'floor'
          });
          runStart = null;
        }
      }
    }

    // Pass 2: Pick up any remaining uncovered single tiles (rare — only when
    // pass 1 skipped width-1 runs that couldn't extend)
    for (let x = 0; x < gridWidth; x++) {
      let runStart = null;
      for (let z = 0; z <= gridHeight; z++) {
        const isFloor = z < gridHeight && grid[x][z] === TILE.FLOOR && !covered[x][z];
        if (isFloor && runStart === null) {
          runStart = z;
        } else if (!isFloor && runStart !== null) {
          defs.push({
            x: x,
            y: -floorHeight,
            z: runStart,
            width: 1,
            height: floorHeight,
            depth: z - runStart,
            materials: DungeonTheme.buildFloorMaterials(theme),
            role: 'floor'
          });
          runStart = null;
        }
      }
    }

    return defs;
  }

  /**
   * Build wall cuboids by run-length merging.
   * Scan horizontally, then fill remaining with vertical runs.
   * @private
   */
  static _buildMergedWalls(grid, gridWidth, gridHeight, theme, wallHeight) {
    const defs = [];
    const covered = [];
    const chunkSize = DungeonConfig.CHUNK_SIZE;
    for (let x = 0; x < gridWidth; x++) {
      covered[x] = new Array(gridHeight).fill(false);
    }

    // Pass 1: Horizontal runs (scan rows), break at chunk boundaries
    for (let z = 0; z < gridHeight; z++) {
      let runStart = null;
      for (let x = 0; x <= gridWidth; x++) {
        const isWall = x < gridWidth && grid[x][z] === TILE.WALL;
        // Break run at chunk boundary so each wall fits in one chunk
        const crossesChunk = runStart !== null && isWall &&
          Math.floor(x / chunkSize) !== Math.floor(runStart / chunkSize);

        if ((!isWall || crossesChunk) && runStart !== null) {
          const runLength = x - runStart;
          if (runLength >= 2) {
            defs.push({
              x: runStart,
              y: 0,
              z: z,
              width: runLength,
              height: wallHeight,
              depth: 1,
              materials: DungeonTheme.buildWallMaterials(theme),
              role: 'wall',
              collisionHeight: DungeonConfig.WALL_COLLISION_HEIGHT
            });
            for (let rx = runStart; rx < x; rx++) {
              covered[rx][z] = true;
            }
          }
          runStart = crossesChunk ? x : null;
        } else if (isWall && runStart === null) {
          runStart = x;
        }
      }
    }

    // Pass 2: Vertical runs (scan columns) for uncovered wall tiles, break at chunk boundaries
    for (let x = 0; x < gridWidth; x++) {
      let runStart = null;
      for (let z = 0; z <= gridHeight; z++) {
        const isWall = z < gridHeight && grid[x][z] === TILE.WALL && !covered[x][z];
        const crossesChunk = runStart !== null && isWall &&
          Math.floor(z / chunkSize) !== Math.floor(runStart / chunkSize);

        if ((!isWall || crossesChunk) && runStart !== null) {
          const runLength = z - runStart;
          defs.push({
            x: x,
            y: 0,
            z: runStart,
            width: 1,
            height: wallHeight,
            depth: runLength,
            materials: DungeonTheme.buildWallMaterials(theme),
            role: 'wall',
            collisionHeight: DungeonConfig.WALL_COLLISION_HEIGHT
          });
          runStart = crossesChunk ? z : null;
        } else if (isWall && runStart === null) {
          runStart = z;
        }
      }
    }

    return defs;
  }

  /**
   * Build vegetation impostor sprite definitions on floor tiles.
   * Uses multi-layer deterministic placement for natural-looking distribution:
   *   - Clump layer: large 8x8 patches of same-species vegetation with soft falloff
   *   - Wall-edge layer: moderate vegetation along walls (boosted within clumps)
   *   - Scatter layer: very sparse isolated accent plants in open areas
   *
   * Sort boxes are pre-computed and clipped against solid geometry (walls, pillars)
   * to guarantee no SAT bounding-box intersections with cuboids.
   *
   * Each def includes a densityRank (0-255) for runtime density filtering.
   * @returns {Array<Object>} Impostor definitions with pre-computed sort boxes
   * @private
   */
  static _buildVegetation(level, theme) {
    const defs = [];
    const vegTypes = DungeonTheme.getVegetation(theme);
    if (!vegTypes || vegTypes.length === 0) return defs;

    const grid = level.grid;
    const W = level.gridWidth;
    const H = level.gridHeight;
    const CLUMP_SIZE = 8;
    const CLIP_GAP = 0.05;
    const MIN_DIM = 0.15;

    // Build skip mask (tiles where vegetation should not be placed)
    const skip = [];
    for (let x = 0; x < W; x++) skip[x] = new Uint8Array(H);

    // Build solid occupancy grid (for sort-box collision checks)
    const solid = [];
    for (let x = 0; x < W; x++) solid[x] = new Uint8Array(H);

    // Mark all wall tiles as solid
    for (let x = 0; x < W; x++) {
      for (let z = 0; z < H; z++) {
        if (grid[x][z] === TILE.WALL) solid[x][z] = 1;
      }
    }

    for (const room of level.rooms) {
      if (room.type === ROOM_TYPE.START || room.type === ROOM_TYPE.EXIT) {
        // Skip entire start/exit rooms
        for (let rx = 0; rx < room.width; rx++) {
          for (let rz = 0; rz < room.depth; rz++) {
            const gx = room.x + rx, gz = room.z + rz;
            if (gx >= 0 && gx < W && gz >= 0 && gz < H) skip[gx][gz] = 1;
          }
        }
      } else {
        // Skip center 3x3 of other rooms (chests, spawns, portals)
        const cx = room.x + Math.floor(room.width / 2);
        const cz = room.z + Math.floor(room.depth / 2);
        for (let dx = -1; dx <= 1; dx++) {
          for (let dz = -1; dz <= 1; dz++) {
            const gx = cx + dx, gz = cz + dz;
            if (gx >= 0 && gx < W && gz >= 0 && gz < H) skip[gx][gz] = 1;
          }
        }
        // Pillars in large rooms: mark solid + skip 3x3 buffer
        if (room.width >= 8 && room.depth >= 8) {
          const offsets = [
            { dx: 2, dz: 2 }, { dx: room.width - 3, dz: 2 },
            { dx: 2, dz: room.depth - 3 }, { dx: room.width - 3, dz: room.depth - 3 }
          ];
          for (const off of offsets) {
            const px = room.x + off.dx, pz = room.z + off.dz;
            if (px >= 0 && px < W && pz >= 0 && pz < H) solid[px][pz] = 1;
            // Skip 3x3 area around pillar
            for (let dx = -1; dx <= 1; dx++) {
              for (let dz = -1; dz <= 1; dz++) {
                const gx = px + dx, gz = pz + dz;
                if (gx >= 0 && gx < W && gz >= 0 && gz < H) skip[gx][gz] = 1;
              }
            }
          }
        }
      }
    }

    // Iterate all floor tiles
    for (let x = 0; x < W; x++) {
      for (let z = 0; z < H; z++) {
        if (grid[x][z] !== TILE.FLOOR) continue;
        if (skip[x][z]) continue;

        const hash = ((x * 2654435761 + z * 40503) >>> 0);

        // Check wall adjacency (any of 8 neighbors)
        let nearWall = false;
        for (let dx = -1; dx <= 1 && !nearWall; dx++) {
          for (let dz = -1; dz <= 1 && !nearWall; dz++) {
            if (dx === 0 && dz === 0) continue;
            const nx = x + dx, nz = z + dz;
            if (nx >= 0 && nx < W && nz >= 0 && nz < H && grid[nx][nz] === TILE.WALL) {
              nearWall = true;
            }
          }
        }

        // Clump noise: 8x8 grid for large organic patches
        const cX = Math.floor(x / CLUMP_SIZE);
        const cZ = Math.floor(z / CLUMP_SIZE);
        const clumpHash = ((cX * 1597334677 + cZ * 2246822507) >>> 0);
        const hasClump = (clumpHash & 0xFF) < 64; // ~25% of regions have a clump

        // Clump center within the 8x8 block
        const clumpCX = cX * CLUMP_SIZE + ((clumpHash >>> 8) % CLUMP_SIZE);
        const clumpCZ = cZ * CLUMP_SIZE + ((clumpHash >>> 14) % CLUMP_SIZE);
        const distToClump = Math.abs(x - clumpCX) + Math.abs(z - clumpCZ);
        const inClump = hasClump && distToClump <= 4;

        // Density threshold (out of 256; higher = denser)
        let threshold;
        if (inClump) {
          // Clump density with soft distance falloff
          if (distToClump === 0) threshold = 45;
          else if (distToClump === 1) threshold = 30;
          else if (distToClump === 2) threshold = 18;
          else if (distToClump === 3) threshold = 10;
          else threshold = 5;
          // Wall bonus: boost density within clumps near walls
          if (nearWall) threshold = Math.min(threshold + 15, 55);
        } else if (nearWall) {
          threshold = 25; // ~10% — moderate along walls
        } else {
          threshold = 3; // ~1% — very sparse open scatter
        }

        if ((hash & 0xFF) >= threshold) continue;

        // Species selection:
        //   - Clumps: consistent type 0 or 1 per patch, accent at edges
        //   - Wall edges (outside clumps): type 2 (low ground-hugging)
        //   - Open scatter: type 3 (accent)
        let typeIndex;
        if (inClump) {
          typeIndex = (clumpHash >>> 16) & 1; // 0 or 1, consistent per clump
          // Accent at clump edges for variety
          if (distToClump >= 3 && ((hash >>> 10) & 0xFF) < 40) typeIndex = 3;
        } else if (nearWall) {
          typeIndex = 2;
        } else {
          typeIndex = 3;
        }
        const veg = vegTypes[typeIndex % vegTypes.length];

        // Sub-tile offset: centered within tile ±0.25
        const ox = ((hash >>> 16) & 0xFF) / 255 * 0.5 - 0.25;
        const oz = ((hash >>> 24) & 0xFF) / 255 * 0.5 - 0.25;

        // Scale variation: 0.75–1.0x base scale for natural variety
        const scaleVar = 0.75 + ((hash >>> 13) & 0xFF) / 255 * 0.25;
        const scale = veg.scale * scaleVar;
        const halfScale = scale / 2;

        const posX = x + 0.5 + ox;
        const posZ = z + 0.5 + oz;

        // Compute sort box, clipped against cardinal solid neighbors
        let sMinX = posX - halfScale;
        let sMaxX = posX + halfScale;
        let sMinZ = posZ - halfScale;
        let sMaxZ = posZ + halfScale;

        if (x + 1 < W && solid[x + 1][z]) sMaxX = Math.min(sMaxX, (x + 1) - CLIP_GAP);
        if (x - 1 >= 0 && solid[x - 1][z]) sMinX = Math.max(sMinX, x + CLIP_GAP);
        if (z + 1 < H && solid[x][z + 1]) sMaxZ = Math.min(sMaxZ, (z + 1) - CLIP_GAP);
        if (z - 1 >= 0 && solid[x][z - 1]) sMinZ = Math.max(sMinZ, z + CLIP_GAP);

        // Enforce minimum sort box size
        if (sMaxX - sMinX < MIN_DIM) { const m = (sMinX + sMaxX) / 2; sMinX = m - MIN_DIM / 2; sMaxX = m + MIN_DIM / 2; }
        if (sMaxZ - sMinZ < MIN_DIM) { const m = (sMinZ + sMaxZ) / 2; sMinZ = m - MIN_DIM / 2; sMaxZ = m + MIN_DIM / 2; }

        // Verify clipped sort box doesn't overlap any solid cell (catches diagonals + pillars)
        const ckMinX = Math.max(0, Math.floor(sMinX));
        const ckMaxX = Math.min(W - 1, Math.floor(sMaxX - 0.001));
        const ckMinZ = Math.max(0, Math.floor(sMinZ));
        const ckMaxZ = Math.min(H - 1, Math.floor(sMaxZ - 0.001));

        let blocked = false;
        for (let bx = ckMinX; bx <= ckMaxX && !blocked; bx++) {
          for (let bz = ckMinZ; bz <= ckMaxZ && !blocked; bz++) {
            if (solid[bx][bz] && sMinX < bx + 1 && sMaxX > bx && sMinZ < bz + 1 && sMaxZ > bz) {
              blocked = true;
            }
          }
        }
        if (blocked) continue;

        const flipped = ((hash >>> 12) & 1) === 1;
        const densityRank = (hash >>> 20) & 0xFF;

        const sortCX = (sMinX + sMaxX) / 2;
        const sortCZ = (sMinZ + sMaxZ) / 2;

        defs.push({
          x: posX,
          y: 0,
          z: posZ,
          textureUrl: veg.texture,
          scale,
          flipped,
          densityRank,
          sortWidth: sMaxX - sMinX,
          sortDepth: sMaxZ - sMinZ,
          sortCenterX: Math.abs(sortCX - posX) > 0.01 ? sortCX : undefined,
          sortCenterZ: Math.abs(sortCZ - posZ) > 0.01 ? sortCZ : undefined
        });
      }
    }

    return defs;
  }
}
